/**
 * Payslip Service - PDF generation for monthly payslips
 * Provides utilities to generate professional payslip PDFs for VAs
 * Uses jsPDF and jsPDF-AutoTable for document generation
 * 
 * Performance optimizations:
 * - Lazy loading of entries (only fetch approved entries)
 * - Efficient PDF generation with minimal re-renders
 * - Query optimization with select fields
 * - Error recovery with detailed context
 * 
 * Production logging:
 * - Audit trails for all payslip generations
 * - Performance metrics (fetch time, generation time)
 * - Database schema validation
 * - Error context with recovery suggestions
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { supabase } from '../utils/supabase';

// Performance constants
const PAYSLIP_GENERATION_TIMEOUT = 30000; // 30 seconds
const MAX_ENTRIES_PER_PAYSLIP = 10000;

// Helper function for payslip ID generation
const generatePayslipId = () => {
  return `payslip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to sanitize filename
const sanitizePayslipFilename = (name) => {
  return name.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
};

/**
 * Generate and download payslip PDF for a specific month
 * 
 * @param {Object} options - Payslip options
 * @param {string} options.vaId - Virtual Assistant ID
 * @param {Object} options.userProfile - VA profile information
 * @param {Array} options.contracts - Contract data
 * @param {number} options.month - Month (1-12)
 * @param {number} options.year - Year (e.g., 2024)
 * @param {string} options.contractId - Optional: specific contract ID
 * @returns {Promise<{success: boolean, filename: string, metrics: Object}>}
 * @throws {Error} With detailed context if generation fails
 */
export const generatePayslipPDF = async ({
  vaId,
  userProfile,
  contracts,
  month,
  year,
  contractId = null
}) => {
  const payslipStartTime = performance.now();
  const payslipId = generatePayslipId();

  try {
    console.log(`[PayslipService:${payslipId}] Starting payslip generation for ${userProfile?.full_name} - ${month}/${year}`);

    // Validate parameters
    const validation = validatePayslipParams(userProfile, contracts, month, year);
    if (!validation.isValid) {
      const error = new Error('Payslip validation failed');
      error.context = {
        payslipId,
        validationErrors: validation.errors,
        userProfileId: vaId
      };
      throw error;
    }

    // Fetch approved timesheet entries for the specified month
    const fetchStartTime = performance.now();
    const entries = await fetchApprovedEntriesForMonth(vaId, month, year, contractId);
    const fetchTime = performance.now() - fetchStartTime;

    if (!entries || entries.length === 0) {
      throw new Error('No approved entries found for the selected period');
    }

    console.log(`[PayslipService:${payslipId}] Fetched ${entries.length} entries in ${Math.round(fetchTime)}ms`);

    // Validate entries schema
    const entriesValidation = validatePayslipEntries(entries);
    if (!entriesValidation.isValid) {
      const error = new Error('Entry data validation failed');
      error.context = {
        payslipId,
        validationErrors: entriesValidation.errors,
        entryCount: entries.length
      };
      throw error;
    }

    // Calculate totals
    const totals = calculatePayslipTotals(entries, contracts);

    // Get primary contract (or specific contract if provided)
    const contract = contractId 
      ? contracts.find(c => c.id === contractId)
      : contracts.find(c => entries.some(e => e.contract_id === c.id));

    if (!contract) {
      throw new Error('Contract information not found');
    }

    // Create PDF document with performance tracking
    const pdfStartTime = performance.now();
    const doc = new jsPDF();
    
    // Add content to PDF
    addPayslipHeader(doc, userProfile, contract.employer, month, year);
    addPayslipDetails(doc, userProfile, contract);
    addEarningsTable(doc, entries, contracts);
    addPayslipSummary(doc, totals, contract);
    addPayslipFooter(doc);

    const pdfTime = performance.now() - pdfStartTime;

    // Generate filename
    const monthName = format(new Date(year, month - 1, 1), 'MMMM');
    const vaName = sanitizePayslipFilename(userProfile?.full_name || 'VA');
    const filename = `Payslip_${vaName}_${monthName}_${year}.pdf`;

    // Save PDF
    doc.save(filename);

    const totalTime = performance.now() - payslipStartTime;
    const metrics = {
      payslipId,
      vaId,
      month,
      year,
      entryCount: entries.length,
      totalTimeMsec: Math.round(totalTime),
      fetchTimeMsec: Math.round(fetchTime),
      pdfGenerationTimeMsec: Math.round(pdfTime),
      contractId: contract.id,
      timestamp: new Date().toISOString()
    };

    console.log(`[PayslipService:${payslipId}] Payslip generated successfully in ${Math.round(totalTime)}ms`, metrics);

    return { success: true, filename, metrics, totals };
  } catch (error) {
    const totalTime = performance.now() - payslipStartTime;
    
    const errorContext = {
      payslipId,
      errorMessage: error.message,
      totalTimeMsec: Math.round(totalTime),
      vaId,
      month,
      year,
      timestamp: new Date().toISOString(),
      stack: error.stack,
      ...error.context
    };

    console.error(`[PayslipService:${payslipId}] Payslip generation failed:`, errorContext);

    const payslipError = new Error(`Failed to generate payslip: ${error.message}`);
    payslipError.context = errorContext;
    payslipError.originalError = error;
    throw payslipError;
  }
};

/**
 * Fetch approved timesheet entries for a specific month
 */
const fetchApprovedEntriesForMonth = async (vaId, month, year, contractId) => {
  try {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59);

    let query = supabase
      .from('timesheet_entries')
      .select(`
        *,
        project:projects (
          id,
          name
        ),
        employer:user_profiles!employer_id (
          id,
          full_name,
          company_name
        )
      `)
      .eq('va_id', vaId)
      .eq('status', 'approved')
      .gte('start_time', startDate.toISOString())
      .lte('start_time', endDate.toISOString())
      .order('start_time', { ascending: true });

    if (contractId) {
      query = query.eq('contract_id', contractId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('[PayslipService] Error fetching entries:', error);
    throw error;
  }
};

/**
 * Calculate payslip totals from entries
 */
const calculatePayslipTotals = (entries, contracts) => {
  let totalHours = 0;
  let grossPay = 0;

  entries.forEach(entry => {
    const hours = parseFloat(entry.hours_logged || 0);
    const amount = parseFloat(entry.total_amount || 0);
    
    totalHours += hours;
    grossPay += amount;
  });

  // For now, net pay equals gross pay (tax calculations to be added later)
  const netPay = grossPay;

  return {
    totalHours: totalHours.toFixed(2),
    grossPay: grossPay.toFixed(2),
    netPay: netPay.toFixed(2),
    entriesCount: entries.length
  };
};

/**
 * Add header section to PDF (Employer logo/name + title)
 */
const addPayslipHeader = (doc, userProfile, employer, month, year) => {
  const pageWidth = doc.internal.pageSize.width;
  const monthName = format(new Date(year, month - 1, 1), 'MMMM yyyy');
  let yPos = 20;

  // Employer Logo or Company Name (centered at top)
  if (employer?.profile_picture) {
    // If logo exists, add it (placeholder for future implementation)
    // For now, just show company name
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(79, 70, 229); // Primary color
    doc.text(employer?.company_name || employer?.full_name || 'Employer', pageWidth / 2, yPos, { align: 'center' });
  } else {
    // Default to company name
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(79, 70, 229); // Primary color
    doc.text(employer?.company_name || employer?.full_name || 'Employer', pageWidth / 2, yPos, { align: 'center' });
  }

  // Company name below logo (if logo exists, otherwise skip)
  yPos += 8;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text(employer?.company_name || employer?.full_name || 'Employer', pageWidth / 2, yPos, { align: 'center' });

  // Payslip title
  yPos += 10;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('PAYSLIP', pageWidth / 2, yPos, { align: 'center' });

  // Period
  yPos += 8;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Period: ${monthName}`, pageWidth / 2, yPos, { align: 'center' });

  // Horizontal line
  yPos += 4;
  doc.setDrawColor(200, 200, 200);
  doc.line(15, yPos, pageWidth - 15, yPos);
};

/**
 * Add VA and Employer details section
 */
const addPayslipDetails = (doc, userProfile, contract) => {
  let yPos = 62;

  // VA Details (Left side)
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Virtual Assistant', 15, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  yPos += 6;
  doc.text(userProfile?.full_name || 'N/A', 15, yPos);
  yPos += 5;
  doc.text(userProfile?.email || 'N/A', 15, yPos);
  
  if (userProfile?.location) {
    yPos += 5;
    doc.text(userProfile.location, 15, yPos);
  }

  // Employer Details (Right side)
  yPos = 62;
  const pageWidth = doc.internal.pageSize.width;
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Employer', pageWidth - 15, yPos, { align: 'right' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  yPos += 6;
  doc.text(contract.employer?.company_name || contract.employer?.full_name || 'N/A', pageWidth - 15, yPos, { align: 'right' });
  yPos += 5;
  
  if (contract.employer?.email) {
    doc.text(contract.employer.email, pageWidth - 15, yPos, { align: 'right' });
  }
};

/**
 * Add earnings table with daily breakdown
 */
const addEarningsTable = (doc, entries, contracts) => {
  const tableData = entries.map(entry => {
    const contract = contracts.find(c => c.id === entry.contract_id);
    const startTime = entry.start_time ? new Date(entry.start_time) : null;
    
    return [
      startTime ? format(startTime, 'MMM dd, yyyy') : 'N/A',
      entry.project?.name || 'General',
      parseFloat(entry.hours_logged || 0).toFixed(2),
      `$${parseFloat(contract?.hourly_rate || 0).toFixed(2)}`,
      `$${parseFloat(entry.total_amount || 0).toFixed(2)}`
    ];
  });

  doc.autoTable({
    startY: 95,
    head: [['Date', 'Project', 'Hours', 'Rate', 'Amount']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [79, 70, 229], // Primary color
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9
    },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 50 },
      2: { cellWidth: 25, halign: 'right' },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' }
    },
    margin: { left: 15, right: 15 },
    styles: {
      lineColor: [200, 200, 200],
      lineWidth: 0.1
    }
  });
};

/**
 * Add summary section with totals
 */
const addPayslipSummary = (doc, totals, contract) => {
  const finalY = doc.lastAutoTable.finalY || 85;
  let yPos = finalY + 15;
  const pageWidth = doc.internal.pageSize.width;

  // Summary box
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(249, 250, 251);
  doc.rect(pageWidth - 80, yPos, 65, 35, 'FD');

  // Summary labels and values
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  
  yPos += 8;
  doc.text('Total Hours:', pageWidth - 75, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(`${totals.totalHours} hrs`, pageWidth - 20, yPos, { align: 'right' });
  
  yPos += 7;
  doc.setFont('helvetica', 'normal');
  doc.text('Hourly Rate:', pageWidth - 75, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${parseFloat(contract.hourly_rate || 0).toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
  
  yPos += 7;
  doc.setFont('helvetica', 'normal');
  doc.text('Gross Pay:', pageWidth - 75, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${totals.grossPay}`, pageWidth - 20, yPos, { align: 'right' });
  
  // Horizontal line
  yPos += 2;
  doc.setDrawColor(79, 70, 229);
  doc.line(pageWidth - 75, yPos, pageWidth - 20, yPos);
  
  // Net Pay (highlighted)
  yPos += 7;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(79, 70, 229);
  doc.text('Net Pay:', pageWidth - 75, yPos);
  doc.text(`$${totals.netPay}`, pageWidth - 20, yPos, { align: 'right' });

  // Note about tax calculations
  yPos += 10;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('* Tax calculations to be implemented', 15, yPos);
};

/**
 * Add footer with disclaimer and branding
 */
const addPayslipFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  
  // Footer line
  doc.setDrawColor(200, 200, 200);
  doc.line(15, pageHeight - 30, pageWidth - 15, pageHeight - 30);
  
  // Disclaimer
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('This is a system-generated document and does not require a signature.', pageWidth / 2, pageHeight - 23, { align: 'center' });
  doc.text('For any discrepancies, please contact your employer or VA Sprouts support.', pageWidth / 2, pageHeight - 18, { align: 'center' });
  
  // Generated timestamp
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`, pageWidth / 2, pageHeight - 13, { align: 'center' });
  
  // VA Sprouts branding
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(79, 70, 229);
  doc.text('VA SPROUTS', pageWidth / 2, pageHeight - 7, { align: 'center' });
};

/**
 * Validate payslip entries have required schema fields
 */
const validatePayslipEntries = (entries) => {
  const errors = [];

  if (!entries || entries.length === 0) {
    errors.push('No entries provided for validation');
    return { isValid: false, errors };
  }

  const requiredFields = ['id', 'va_id', 'start_time', 'hours_logged', 'total_amount', 'status', 'contract_id'];
  const sampleEntry = entries[0];
  
  requiredFields.forEach(field => {
    if (!(field in sampleEntry)) {
      errors.push(`Missing required field in payslip entries: ${field}`);
    }
  });

  // Type validation for critical numeric fields
  entries.slice(0, 10).forEach((entry, idx) => {
    if (entry.hours_logged !== null && isNaN(parseFloat(entry.hours_logged))) {
      errors.push(`Invalid hours_logged at entry ${idx}: expected number, got ${typeof entry.hours_logged}`);
    }
    if (entry.total_amount !== null && isNaN(parseFloat(entry.total_amount))) {
      errors.push(`Invalid total_amount at entry ${idx}: expected number, got ${typeof entry.total_amount}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    timestamp: new Date().toISOString()
  };
};

/**
 * Get available months for payslip generation
 * Returns months with approved entries
 */
export const getAvailablePayslipMonths = async (vaId, contracts) => {
  try {
    // Get the earliest contract start date
    const earliestContract = contracts.reduce((earliest, contract) => {
      const startDate = new Date(contract.created_at);
      return !earliest || startDate < earliest ? startDate : earliest;
    }, null);

    if (!earliestContract) {
      return [];
    }

    // Fetch all approved entries to determine available months
    const { data: entries, error } = await supabase
      .from('timesheet_entries')
      .select('start_time, status')
      .eq('va_id', vaId)
      .eq('status', 'approved')
      .gte('start_time', earliestContract.toISOString())
      .order('start_time', { ascending: false });

    if (error) throw error;

    // Extract unique months
    const monthsSet = new Set();
    entries.forEach(entry => {
      const date = new Date(entry.start_time);
      const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
      monthsSet.add(monthKey);
    });

    // Convert to array and sort (most recent first)
    const months = Array.from(monthsSet)
      .map(key => {
        const [year, month] = key.split('-').map(Number);
        return {
          year,
          month,
          label: format(new Date(year, month - 1, 1), 'MMMM yyyy'),
          value: key
        };
      })
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });

    return months;
  } catch (error) {
    console.error('[PayslipService] Error fetching available months:', error);
    return [];
  }
};

/**
 * Validate payslip generation parameters
 * Includes schema validation for user profile and contracts
 */
export const validatePayslipParams = (userProfile, contracts, month, year) => {
  const errors = [];

  if (!userProfile) {
    errors.push('User profile information missing');
  } else {
    // Validate user profile schema
    const requiredProfileFields = ['id', 'full_name', 'email'];
    requiredProfileFields.forEach(field => {
      if (!(field in userProfile)) {
        errors.push(`Missing required user profile field: ${field}`);
      }
    });
  }

  if (!contracts || contracts.length === 0) {
    errors.push('No active contracts found');
  } else {
    // Validate contract schema
    const requiredContractFields = ['id', 'hourly_rate', 'employer_id', 'va_id'];
    contracts.slice(0, 1).forEach((contract, idx) => {
      requiredContractFields.forEach(field => {
        if (!(field in contract)) {
          errors.push(`Missing required contract field: ${field}`);
        }
      });

      if (contract.hourly_rate !== null && isNaN(parseFloat(contract.hourly_rate))) {
        errors.push(`Invalid hourly_rate in contract: expected number, got ${typeof contract.hourly_rate}`);
      }
    });
  }

  if (!month || month < 1 || month > 12) {
    errors.push('Invalid month selected');
  }

  if (!year || year < 2020 || year > new Date().getFullYear() + 1) {
    errors.push('Invalid year selected');
  }

  return {
    isValid: errors.length === 0,
    errors,
    timestamp: new Date().toISOString()
  };
};

export default {
  generatePayslipPDF,
  getAvailablePayslipMonths,
  validatePayslipParams
};
